import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import iconButtonStyle from './style'
import Icon, { IconType } from '../Icon/Icon'
import IconNameType from '../../helpers/types/IconName.interface'
import { VerbalColorType } from '../../../types'
import { useThemeContext } from 'providers'

interface PropsInterface extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'name'> {
    name: IconNameType;
    size?: 'small' | 'medium' | 'large';
    color?: VerbalColorType; // for contained, contained-light button it can be sent as primary-200, for icon base color only
    variant?: 'contained' | 'contained-light' | 'icon';
    'data-testid'?: string;
    iconProps?: Omit<IconType, 'name'>
}

const iconSizes = {
    small: 18,
    medium: 20,
    large: 24
}

const sumOrMaxShade = (s1: string | number, s2: number): number => {
    if (!Number.isInteger(s1)) s1 = 0

    const maxShade = 900
    let sum = s1 + s2
    if ((sum % 100) !== 0) {
        sum = Math.ceil(sum / 100) * 100
    }
    if (sum <= maxShade) return sum

    return maxShade
}

const getShadesPerVariant = (variant, base = 400) => {
    if (variant === 'contained') {
        const icon = '#FFFFFF'
        return {
            inactive: {
                bg: base,
                icon
            },
            hover: {
                bg: sumOrMaxShade(base, 100), // hover by 100 darker than inactive
                icon
            },
            active: {
                bg: sumOrMaxShade(base, 200),
                icon
            },
            disabled: {
                bg: 100,
                icon
            }
        }
    }

    if (variant === 'contained-light') {
        return {
            inactive: {
                bg: base,
                icon: sumOrMaxShade(base, 350)
            },
            hover: {
                bg: sumOrMaxShade(base, 50),
                icon: sumOrMaxShade(base, 350)
            },
            active: {
                bg: sumOrMaxShade(base, 150),
                icon: sumOrMaxShade(base, 450)
            },
            disabled: {
                bg: 50,
                icon: 200
            }
        }
    }

    if (variant === 'icon') {
        return {
            inactive: {
                bg: 'transparent',
                icon: sumOrMaxShade(base, 400)
            },
            hover: {
                bg: sumOrMaxShade(base, 50),
                icon: sumOrMaxShade(base, 400)
            },
            active: {
                bg: sumOrMaxShade(base, 100),
                icon: sumOrMaxShade(base, 500)
            },
            disabled: {
                bg: 0,
                icon: 200
            }
        }
    }

    throw new Error('Variant/shade not supported')
}

const getBaseColorAndShade = (variant, colors, color) => {
    if (
        // ['contained', 'contained-light'].includes(variant) &&
        color.includes('-')
    ) {
        const [c, s] = color.split('-')

        if (colors[c]?.[s] && !Number.isNaN(s)) {
            return {
                color: c,
                shade: Number.parseInt(s)
            }
        }
    }
    const c = (typeof color === 'string' && colors[color]) ? color : 'primary'

    if (variant === 'contained') {
        return {
            color: c,
            shade: 400
        }
    }

    if (variant === 'contained-light') {
        return {
            color: c,
            shade: 50
        }
    }

    if (variant === 'icon') {
        return {
            color: c,
            shade: 'transparent'
        }
    }

    throw new Error('Variant/shade not supported')
}

const useStyles = makeStyles(iconButtonStyle)

const renderIcon = (icon, btnSize, color, iconProps) => {
    if (!icon) return ''
    if (typeof icon === 'string') {
        return <Icon className='button-icon__icon' size={iconSizes[btnSize]} color={color} name={icon} {...iconProps} />
    }

    return icon
}

const getShade = (colors, color, shade) => {
    if (typeof shade === 'string') return shade
    if (!colors[color]) return color

    return colors?.[color]?.[shade] || '#FFFFFF'
}

/**
 *
 */
const IconButton: React.FC<PropsInterface> = ({
    name,
    size = 'medium',
    variant = 'contained',
    color = 'primary',
    style = {},
    iconProps = {},
    'data-testid': dataTestId = 'icon-button',
    ...props
}): JSX.Element => {
    const themeContext = useThemeContext()
    const { colors } = themeContext?.['foundations-library'] ?? {}

    const styles = useStyles()

    const {
        shades,
        baseColor,
        baseInactiveShade
    } = useMemo(() => {
        const {
            color: baseColor,
            shade: baseInactiveShade
        } = getBaseColorAndShade(variant, colors, color)
        return {
            shades: getShadesPerVariant(variant, baseInactiveShade),
            baseColor: props.disabled ? 'neutral' : baseColor,
            baseInactiveShade
        }
    }, [color, variant, props.disabled])

    const getDefaultShade = useCallback((type) => {
        if (props.disabled) {
            return getShade(colors, 'neutral', shades.disabled[type])
        }
        return getShade(colors, baseColor, baseInactiveShade)
    }, [props.disabled, color])

    const [buttonColor, setButtonColor] = useState(getDefaultShade('bg'))
    const [iconColor, setIconColor] = useState(getDefaultShade('icon'))
    const [isMouseOver, setIsMouseOver] = useState(false)

    const updateStyles = (type: 'active' | 'inactive' | 'hover' | 'disabled', mouseUp = false) => {
        if (mouseUp && isMouseOver) {
            setButtonColor(getShade(colors, baseColor, shades.hover.bg))
            setIconColor(getShade(colors, baseColor, shades.hover.icon))
        } else {
            setButtonColor(getShade(colors, baseColor, shades[type].bg))
            setIconColor(getShade(colors, baseColor, shades[type].icon))
        }
    }

    const onMouseDown = (e) => {
        updateStyles('active')
        if (props.onMouseDown) props.onMouseDown(e)
    }
    const onMouseUp = (e) => {
        updateStyles('inactive', true)
        if (props.onMouseUp) props.onMouseUp(e)
        if (props.onClick) props.onClick(e)
    }
    const onMouseEnter = (e) => {
        updateStyles('hover')
        if (props.onMouseEnter) props.onMouseEnter(e)
        setIsMouseOver(true)
    }
    const onMouseLeave = (e) => {
        updateStyles('inactive')
        if (props.onMouseLeave) props.onMouseLeave(e)
        setIsMouseOver(false)
    }

    useEffect(() => {
        updateStyles(props.disabled ? 'disabled' : 'inactive')
    }, [props.disabled])

    let classes = [color, size, variant].filter((x) => x).map((x) => `button--${x}`).join(' ')
    if (props.className) classes = `${classes} ${props.className}`

    return <button
        {...props}
        className={`${styles.iconButton} ${classes}`}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{ background: buttonColor, ...style }}
        onClick={() => undefined}
        data-testid={dataTestId}
    >
        {renderIcon(name, size, iconColor, iconProps)}
    </button>
}
/**
 *
 */
export type IconButtonType = PropsInterface
export default IconButton
